<template>
  <main class="page">
    <div class="stagger">
      <header class="today-top">
        <div>
          <h1>今日</h1>
          <p class="today-date">{{ todayLabel }} · {{ name }}</p>
        </div>
        <div class="header-actions">
          <router-link class="icon-button" to="/customer" aria-label="查找客户">
            <app-icon name="search" />
          </router-link>
          <router-link class="icon-button solid" to="/customer/check" aria-label="新增客户">
            <app-icon name="plus" />
          </router-link>
        </div>
      </header>

      <section class="today-overview" aria-label="今日数据">
        <div class="sec-head">
          <h2>今日数据</h2>
          <router-link to="/task">我的任务<app-icon name="arrow" /></router-link>
        </div>
        <div class="today-stats">
          <button type="button" @click="showNew">
            <strong>{{ count('new') }}</strong>
            <span>今日新增</span>
          </button>
          <button type="button" @click="showVisits">
            <strong>{{ count('comeExpect') }}</strong>
            <span>预计来访</span>
          </button>
          <div>
            <strong>{{ count('successToday') }}</strong>
            <span>今日成交</span>
          </div>
        </div>
      </section>

      <daily-brief-card />

      <section class="progress">
        <div class="progress-label">
          到期待跟进 {{ waitText }} 位
          <span v-if="doneRate !== null">今日完成 {{ doneRate }}%</span>
        </div>
        <div class="progress-bar"><i :style="{ width: (doneRate || 0) + '%' }" /></div>
      </section>

      <div v-if="error" class="error-message">
        {{ error }}
        <button class="text-button" @click="load">重试</button>
      </div>
      <p v-if="syncError" class="error-message" role="status">{{ syncError }}<button class="text-button" :disabled="syncing" @click="syncChangedCustomers">重试同步</button></p>
      <p v-if="statsError" class="error-message" role="status">{{ statsError }}<button class="text-button" @click="loadCounts(true)">重试统计</button></p>
      <div v-if="loading && !customers.length" class="loading-state">
        <span class="spinner" />
        正在读取今天的安排
      </div>

      <section v-if="focus" class="focus" :class="phase" :data-customer-id="focus.id">
        <div class="focus-body">
          <div class="focus-row">
            <span class="focus-tag">下一位</span>
            <span v-if="$store.state.customerChanges[focus.id]" class="focus-tag">本次已更新</span>
            <span class="focus-due" :class="{ late: focusDays < 0 }">{{ focusDueText }}</span>
            <button v-if="customers.length > 1" type="button" class="focus-skip" @click="skip">换一位</button>
          </div>
          <button type="button" class="focus-need" @click="open(focus)">{{ need(focus) }}</button>
          <p class="focus-who">
            <span v-if="focus.labels_text" class="focus-grade">{{ focus.labels_text }}</span>{{ focusMeta }}
          </p>
          <button type="button" class="focus-note" @click="open(focus)">
            <b>{{ focusNoteHead }}</b>
            <span>{{ clean(focus.log_content) || '打通后记下客户想要什么、什么时候方便看房。' }}</span>
          </button>
          <div class="focus-acts">
            <a v-if="callable(focus)" class="button call" :href="'tel:' + focus.mobile">
              <app-icon name="phone" />
              拨打电话
            </a>
            <button v-else type="button" class="button call" disabled>号码受权限保护</button>
            <button type="button" class="button ghost" @click="follow(focus)">写跟进</button>
          </div>
        </div>
      </section>
      <section v-else-if="!loading && !error" class="focus focus-done">
        <strong>当前没有到期的跟进</strong>
        <p>可以查看后续日程，或为客户安排下一次联系。</p>
        <div class="focus-acts">
          <router-link class="button call" to="/schedule">查看日程</router-link>
          <router-link class="button ghost" to="/customer">看客户</router-link>
        </div>
      </section>

      <div v-if="dueToday.length" class="sec-head">
        <h2>今天到期</h2>
        <span>{{ dueToday.length }} 位</span>
      </div>
      <div v-if="dueToday.length" class="c-list">
        <customer-card v-for="item in dueToday" :key="item.id" :customer="item" @open="open" />
      </div>

      <div v-if="overdue.length" class="sec-head">
        <h2>逾期待补</h2>
        <router-link to="/remind">全部<app-icon name="arrow" /></router-link>
      </div>
      <div v-if="overdue.length" class="c-list">
        <customer-card v-for="item in overdue" :key="item.id" :customer="item" @open="open" />
      </div>
      <router-link v-if="total > shown" class="all-link" to="/remind">
        查看全部到期客户
        <span>共 {{ total }} 位</span>
      </router-link>

    </div>
  </main>
</template>
<script>
import DailyBriefCard from '../components/daily-brief-card'
import { canCall } from '../common/customer'
import { capturePosition, restorePosition, changesAfter, patchCustomers, syncCustomers } from '../common/customer-sync'
import {
  daysFromToday,
  dateTimeText,
  needText,
  tail,
  today,
  weekday
} from '../common/when'
export default {
  components: { DailyBriefCard },
  data() {
    const now = new Date()
    return {
      counts: {},
      customers: [],
      total: 0,
      index: 0,
      phase: '',
      loading: false,
      error: '',
      syncError: '',
      statsError: '',
      syncing: false,
      customerVersion: 0,
      countsVersion: 0,
      initialized: false,
      todayLabel: now.getMonth() + 1 + '月' + now.getDate() + '日 ' + weekday(today())
    }
  },
  computed: {
    name() {
      return (this.$store.state.profile && this.$store.state.profile.name) || '同事'
    },
    remindCount() {
      return this.counts.remindUnfinished || null
    },
    waitText() {
      return this.remindCount ? this.remindCount.num : this.total || '—'
    },
    // 后端给的是未完成占比，换成今日完成度
    doneRate() {
      const rate = this.remindCount && Number(this.remindCount.rate)
      return rate || rate === 0 ? Math.max(0, Math.min(100, 100 - rate)) : null
    },
    focus() {
      return this.customers.length ? this.customers[this.index % this.customers.length] : null
    },
    focusDays() {
      return this.focus ? daysFromToday(this.focus.log_time) : null
    },
    focusDueText() {
      const days = this.focusDays
      if (days === null) return '还没有安排跟进时间'
      if (days < 0) return '逾期 ' + -days + ' 天'
      if (days === 0) return '今天到期'
      return '下次 ' + dateTimeText(this.focus.log_time)
    },
    focusMeta() {
      const c = this.focus
      return [c.name || '未填写姓名', tail(c.mobile), c.status_text, c.total_budget_range_text || c.purpose_v2_text]
        .filter(Boolean)
        .join(' · ')
    },
    focusNoteHead() {
      if (!this.clean(this.focus.log_content)) return '还没有跟进记录'
      return '上次跟进' + (this.focus.save_time ? ' · ' + this.focus.save_time : '')
    },
    rest() {
      const focus = this.focus
      return this.customers.filter(item => item !== focus)
    },
    dueToday() {
      return this.rest.filter(item => {
        const days = daysFromToday(item.log_time)
        return days !== null && days >= 0
      })
    },
    overdue() {
      return this.rest.filter(item => daysFromToday(item.log_time) < 0).slice(0, 5)
    },
    shown() {
      return (this.focus ? 1 : 0) + this.dueToday.length + this.overdue.length
    }
  },
  created() {
    const cache = this.$store.state.views.workbench
    if (cache && cache.initialized && cache.revision === this.$store.state.revision) {
      this.counts = cache.counts; this.customers = cache.customers; this.total = cache.total
      this.index = cache.index; this.customerVersion = cache.customerVersion
      this.countsVersion = cache.countsVersion || 0; this.initialized = true
      restorePosition(this, cache)
      this.$nextTick(() => window.requestAnimationFrame(() => this.syncChangedCustomers()))
    } else this.load()
  },
  beforeRouteLeave(to, from, next) {
    this.$store.state.views.workbench = Object.assign({ counts: this.counts, customers: this.customers, total: this.total,
      index: this.index, revision: this.$store.state.revision, customerVersion: this.customerVersion,
      countsVersion: this.countsVersion, initialized: this.initialized }, capturePosition())
    next()
  },
  methods: {
    count(key) {
      return this.counts[key] ? this.counts[key].num : '—'
    },
    need(item) {
      return needText(item)
    },
    clean(value) {
      return String(value || '')
        .replace(/<[^>]+>/g, ' ')
        .trim()
    },
    callable(item) {
      return canCall(item.mobile)
    },
    async load() {
      if (this.loading) return
      this.loading = true
      this.error = ''
      try {
        const data = await Promise.all([
          this.$api.post('/api/employee/counts', { refresh: this.$store.state.customerRevision > 0 }),
          this.$api.post('/api/employee/remind/list', { loaded_ids: [] })
        ])
        if (this._isDestroyed) return
        this.counts = data[0].counts || {}
        this.customers = data[1].data || []
        const pagination = data[1].meta && data[1].meta.pagination
        this.total = data[1].meta && data[1].meta.sync ? data[1].meta.sync.total : pagination ? pagination.total : this.customers.length
        this.index = 0
        this.customerVersion = this.$store.state.customerRevision
        this.countsVersion = this.customerVersion; this.initialized = true
      } catch (e) {
        this.error = e.message
      } finally {
        this.loading = false
      }
    },
    async loadCounts(fresh) {
      this.statsError = ''
      const version = this.$store.state.customerRevision
      try {
        const response = await this.$api.post('/api/employee/counts', { refresh: !!fresh })
        if (!this._isDestroyed) { this.counts = response.counts || {}; this.countsVersion = version }
      } catch (e) { if (!this._isDestroyed) this.statsError = '统计暂未更新，客户跟进已保存' }
    },
    async syncChangedCustomers() {
      if (this.syncing || this._isDestroyed) return
      const version = this.$store.state.customerRevision
      const changes = changesAfter(this.$store, this.customerVersion || 0)
      if (!changes.length) { if (this.countsVersion !== version) this.loadCounts(true); return }
      this.syncing = true; this.syncError = ''
      const focusedId = this.focus && this.focus.id
      const previousIds = this.customers.map(item => item.id)
      const shouldAdvance = changes.some(change => change.kind === 'followup' && Number(change.id) === Number(focusedId))
      const position = capturePosition()
      this.customers = patchCustomers(this.customers, changes)
      restorePosition(this, position)
      try {
        const result = await syncCustomers(this.$api, '/api/employee/remind/list', {}, this.customers, changes)
        if (this._isDestroyed || version !== this.$store.state.customerRevision) return
        const anchor = capturePosition()
        this.customers = result.list; this.total = result.total
        if (!this.customers.length && this.total > 0) {
          const response = await this.$api.post('/api/employee/remind/list', { loaded_ids: [] })
          if (this._isDestroyed || version !== this.$store.state.customerRevision) return
          this.customers = response.data || []
          if (response.meta && response.meta.sync) this.total = response.meta.sync.total
        }
        let index = this.customers.findIndex(item => Number(item.id) === Number(focusedId))
        if (shouldAdvance || index < 0) {
          const start = previousIds.indexOf(focusedId)
          const order = previousIds.slice(start + 1).concat(previousIds.slice(0, start + 1))
          const available = order.filter(id => id !== focusedId && this.customers.some(item => item.id === id))
          const next = available.find(id => !this.$store.state.customerChanges[id] || this.$store.state.customerChanges[id].kind !== 'followup') || available[0]
          index = this.customers.findIndex(item => item.id === next)
        }
        this.index = Math.max(0, index); this.phase = ''
        this.customerVersion = version
        restorePosition(this, anchor)
        await this.loadCounts(true)
      } catch (e) { if (!this._isDestroyed) this.syncError = '客户已保存，待跟进队列暂未同步' }
      finally {
        this.syncing = false
        if (!this._isDestroyed && version !== this.$store.state.customerRevision) this.syncChangedCustomers()
      }
    },
    // 只在本机换一位，不改变客户的跟进安排
    skip() {
      if (this.phase) return
      this.phase = 'leaving'
      setTimeout(() => {
        this.index = (this.index + 1) % this.customers.length
        this.phase = 'arriving'
        setTimeout(() => (this.phase = ''), 360)
      }, 220)
    },
    open(item) {
      this.$router.push('/customer/detail/' + item.id + '/workbench')
    },
    follow(item) {
      this.$router.push('/customer/edit/' + item.id + '/workbench')
    },
    showNew() {
      this.showPeriod('created_at')
    },
    showVisits() {
      this.showPeriod('come_time')
    },
    showPeriod(order) {
      const y = today()
      Object.assign(this.$store.state.search, {
        searchOrder: order,
        searchStartTime: y + ' 00:00:00',
        searchEndTime: y + ' 23:59:59',
        searchKeyword: '',
        searchStatus: '',
        searchLabels: '',
        searchSubjects: [],
        searchEmployees: [],
        searchChannels: [],
        searchSourceChannelV2: [],
        searchTotalBudgetRange: [],
        searchPriceAcceptanceLevel: [],
        searchPurposeV2: []
      })
      this.$router.push('/customer')
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/pages/workbench.vue